import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from '@fikasio/react-ui-components';
import { setPaneContent } from 'services/pane/slice';
import { useAddTaskMutation, useGetTasksQuery, usePatchTaskMutation } from 'services/tasks/api';
import { Task } from 'services/tasks/types';
import CompletionTimeEntryModal from 'components/tasks/CompletionTimeEntryModal';
import TaskStatusDropdown, { OpenStatusDropdownProvider } from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { isNewlyCompleted, statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';
import styles from './TaskSubtasks.module.css';

interface TaskSubtasksProps {
  parentTask: Task;
}

const TaskSubtasks = ({ parentTask }: TaskSubtasksProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [draftKey, setDraftKey] = useState(0);
  const [completionTask, setCompletionTask] = useState<Task | null>(null);
  const { data: subtasksPage } = useGetTasksQuery({
    filter: {
      isArchived: false,
      parentTaskId: parentTask.id,
    },
    sort: { dueAt: 1, description: 1 },
  }, { skip: !parentTask.id });
  const [createTask] = useAddTaskMutation();
  const [patchTask] = usePatchTaskMutation();
  const subtasks = subtasksPage?.tasks ?? [];

  const addSubtask = async (value: string) => {
    const description = value.trim();
    if (!parentTask.id || !description) {
      return;
    }
    await createTask({
      description,
      parentTaskId: parentTask.id,
      type: 'Template',
      ...(parentTask.projects ? { projects: parentTask.projects } : {}),
    });
    setDraftKey(key => key + 1);
  };

  return (
    <OpenStatusDropdownProvider>
      <div className={styles.subtasks}>
      <div className={styles.heading}>{t('subtasks')}</div>
      {
        subtasks.map(subtask => (
          <div
            className={styles.row}
            key={subtask.id}
          >
            <TaskStatusDropdown
              onChange={(status, extras) => {
                if (subtask.id) {
                  patchTask({
                    id: subtask.id,
                    ...statusChangePatch(status, {
                      cancelReason: extras?.cancelReason,
                      wasArchived: subtask.isArchived,
                    }),
                  });
                  if (isNewlyCompleted(subtask.status, status)) {
                    setCompletionTask(subtask);
                  }
                }
              }}
              status={visibleStatus(subtask.status, subtask.isArchived)}
            />
            <button
              className={styles.description}
              onClick={() => {
                if (subtask.id) {
                  dispatch(setPaneContent({
                    type: 'TASK',
                    id: subtask.id,
                  }));
                }
              }}
              type="button"
            >
              {subtask.description || t('createATask')}
            </button>
          </div>
        ))
      }
      <Input
        key={draftKey}
        className={styles.addField}
        defaultValue=""
        onSave={addSubtask}
        placeholder={t('addSubtask')}
        style={{
          height: 28,
          padding: '2px 8px',
          width: '100%',
        }}
        />
      {
        completionTask?.id && (
          <CompletionTimeEntryModal
            onHide={() => setCompletionTask(null)}
            onSave={patch => {
              patchTask({
                id: completionTask.id,
                ...patch,
              });
            }}
            task={completionTask}
          />
        )
      }
      </div>
    </OpenStatusDropdownProvider>
  );
};

export default TaskSubtasks;
